import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Copy, Loader2, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const DIAS = ["seg", "ter", "qua", "qui", "sex", "sab", "dom"];
const DIA_LABEL = { seg: "Segunda-feira", ter: "Terça-feira", qua: "Quarta-feira", qui: "Quinta-feira", sex: "Sexta-feira", sab: "Sábado", dom: "Domingo" };
const DIA_SHORT = { seg: "Seg", ter: "Ter", qua: "Qua", qui: "Qui", sex: "Sex", sab: "Sáb", dom: "Dom" };

const emptyDias = () => Object.fromEntries(DIAS.map((d) => [d, [["00:00", "00:00"], ["00:00", "00:00"], ["00:00", "00:00"]]]));

const fmtPeriodos = (periodos) => {
  const ativos = (periodos || []).filter(([i, f]) => i !== "00:00" || f !== "00:00");
  return ativos.length ? ativos.map(([i, f]) => `${i}-${f}`).join(" / ") : "—";
};

export default function HorariosPage() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [descricao, setDescricao] = useState("");
  const [dias, setDias] = useState(emptyDias());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/tenant/horarios").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const filtered = (items || []).filter((h) => {
    const text = q.trim().toLowerCase();
    if (!text) return true;
    return h.descricao.toLowerCase().includes(text) || String(h.codigo) === text;
  });

  const openNew = () => {
    setEditing(null);
    setDescricao("");
    setDias(emptyDias());
    setOpen(true);
  };

  const openEdit = (h) => {
    setEditing(h);
    setDescricao(h.descricao);
    const base = emptyDias();
    for (const d of DIAS) base[d] = (h.dias?.[d] || base[d]).map((p) => [...p]);
    setDias(base);
    setOpen(true);
  };

  const setPeriodo = (dia, idx, pos, value) => {
    setDias((prev) => {
      const next = { ...prev, [dia]: prev[dia].map((p) => [...p]) };
      next[dia][idx][pos] = value;
      return next;
    });
  };

  const copyDown = (idx) => {
    if (idx >= DIAS.length - 1) return;
    setDias((prev) => ({ ...prev, [DIAS[idx + 1]]: prev[DIAS[idx]].map((p) => [...p]) }));
    toast.success(`Períodos copiados para ${DIA_LABEL[DIAS[idx + 1]]}`);
  };

  const clearRow = (dia) => {
    setDias((prev) => ({ ...prev, [dia]: [["00:00", "00:00"], ["00:00", "00:00"], ["00:00", "00:00"]] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/tenant/horarios/${editing.id}`, { descricao, dias });
        toast.success("Horário atualizado");
      } else {
        await api.post("/tenant/horarios", { descricao, dias });
        toast.success("Horário cadastrado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tenant/horarios/${deleteTarget.id}`);
      toast.success("Horário excluído");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDeleting(false);
    }
  };

  if (!items) return <Spinner />;

  return (
    <div data-testid="horarios-page">
      <PageHeader title="Horários" subtitle={`${items.length} faixa(s) cadastrada(s) — horários permitidos por dia da semana para reserva de áreas comuns`}>
        <Button data-testid="new-horario-button" onClick={openNew}
          className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
          <Plus className="h-4 w-4 mr-1.5" /> Incluir
        </Button>
      </PageHeader>

      {items.length > 0 && (
        <div className="relative mb-4">
          <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input data-testid="horario-filter-q" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por descrição ou código…"
            className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon={Clock} title={items.length ? "Nenhum horário encontrado" : "Nenhum horário cadastrado"}
          subtitle="Cadastre faixas de horário por dia da semana para controlar a reserva das áreas comuns." />
      ) : (
        <div className="space-y-3">
          {filtered.map((h) => (
            <div key={h.id} data-testid={`horario-row-${h.id}`}
              className="bg-zinc-900 border border-zinc-800 rounded-md px-4 py-3 flex items-start gap-4">
              <div className="w-10 shrink-0 pt-0.5">
                <span className="font-mono text-xs text-zinc-500" data-testid={`horario-codigo-${h.id}`}>{h.codigo}</span>
              </div>
              <div className="w-40 shrink-0 pt-0.5">
                <p className="text-sm font-medium text-zinc-100" data-testid={`horario-descricao-${h.id}`}>{h.descricao}</p>
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-[11px] font-mono text-zinc-400" data-testid={`horario-resumo-${h.id}`}>
                {DIAS.map((d) => (
                  <p key={d}>
                    <span className="text-zinc-500">{DIA_SHORT[d]}:</span> {fmtPeriodos(h.dias?.[d])}
                  </p>
                ))}
              </div>
              <div className="shrink-0 flex items-center gap-1">
                <Button data-testid={`horario-edit-${h.id}`} variant="ghost" size="icon" title="Editar horário"
                  onClick={() => openEdit(h)}
                  className="h-8 w-8 text-zinc-400 hover:text-indigo-400 hover:bg-zinc-800">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button data-testid={`horario-delete-${h.id}`} variant="ghost" size="icon" title="Excluir horário"
                  onClick={() => setDeleteTarget(h)}
                  className="h-8 w-8 text-zinc-400 hover:text-rose-400 hover:bg-zinc-800">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="horario-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">{editing ? `Editar Horário #${editing.codigo}` : "Novo Horário"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="horario-form">
            <div>
              <Label className="text-xs text-zinc-400">Descrição *</Label>
              <Input data-testid="horario-descricao-input" required value={descricao} maxLength={100}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder='Ex.: "24h", "Prestadores", "Horário comercial"'
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3" data-testid="horario-dias-section">
              <p className="text-xs font-medium text-zinc-400 mb-3">Faixas de Horário por Dia de Semana</p>
              <table className="w-full">
                <thead>
                  <tr className="text-left">
                    <th className="text-[11px] text-zinc-500 font-medium pb-2 pr-2">Dia</th>
                    <th className="text-[11px] text-zinc-500 font-medium pb-2 pr-2">Período 1</th>
                    <th className="text-[11px] text-zinc-500 font-medium pb-2 pr-2">Período 2</th>
                    <th className="text-[11px] text-zinc-500 font-medium pb-2 pr-2">Período 3</th>
                    <th className="text-[11px] text-zinc-500 font-medium pb-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {DIAS.map((dia, di) => (
                    <tr key={dia} data-testid={`horario-dia-${dia}`} className="border-t border-zinc-800/60">
                      <td className="py-2 pr-2 text-xs text-zinc-300 whitespace-nowrap">{DIA_LABEL[dia]}</td>
                      {[0, 1, 2].map((pi) => (
                        <td key={pi} className="py-2 pr-2">
                          <span className="flex items-center gap-1">
                            <Input type="time" data-testid={`horario-${dia}-p${pi}-inicio`} value={dias[dia][pi][0]}
                              onChange={(e) => setPeriodo(dia, pi, 0, e.target.value)}
                              className="h-8 w-24 px-1.5 text-xs bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md [color-scheme:dark]" />
                            <span className="text-zinc-600 text-xs">→</span>
                            <Input type="time" data-testid={`horario-${dia}-p${pi}-fim`} value={dias[dia][pi][1]}
                              onChange={(e) => setPeriodo(dia, pi, 1, e.target.value)}
                              className="h-8 w-24 px-1.5 text-xs bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md [color-scheme:dark]" />
                          </span>
                        </td>
                      ))}
                      <td className="py-2 text-right whitespace-nowrap">
                        {di < DIAS.length - 1 && (
                          <Button type="button" data-testid={`horario-copy-${dia}`} variant="ghost" size="icon"
                            title={`Copiar para ${DIA_LABEL[DIAS[di + 1]]}`} onClick={() => copyDown(di)}
                            className="h-7 w-7 text-zinc-500 hover:text-indigo-400 hover:bg-zinc-800">
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button type="button" data-testid={`horario-clear-${dia}`} variant="ghost" size="icon"
                          title="Limpar dia (zera todos os períodos)" onClick={() => clearRow(dia)}
                          className="h-7 w-7 text-zinc-500 hover:text-rose-400 hover:bg-zinc-800">
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-3 pt-1">
              <p className="text-[11px] text-zinc-500" data-testid="horario-form-hint">
                3 períodos por dia (início/fim). 00:00 às 00:00 = sem faixa.
              </p>
              <div className="flex justify-end gap-2 shrink-0">
                <Button type="button" data-testid="horario-cancel-button" variant="ghost" onClick={() => setOpen(false)}
                  className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
                  Cancelar
                </Button>
                <Button data-testid="horario-save-button" type="submit" disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Excluir horário</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Tem certeza que deseja excluir <span className="text-zinc-200 font-medium">{deleteTarget?.descricao}</span>? A exclusão fica registrada na auditoria.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2" data-testid="delete-horario-dialog">
            <Button data-testid="delete-horario-cancel" variant="ghost" onClick={() => setDeleteTarget(null)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Cancelar
            </Button>
            <Button data-testid="delete-horario-confirm" onClick={deleteItem} disabled={deleting}
              className="bg-rose-600 hover:bg-rose-500 text-white rounded-md transition-colors duration-200">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
